import { APPLICATION_CONFIG } from "@constants/constants";
import { useLoginRequest } from "@features/auth/api/use-login-request";
import { zodResolver } from "@hookform/resolvers/zod";
import EyeIcon from "@icons/eye-open.svg?react";
import GlobalIcon from "@icons/global.svg?react";
import Logo from "@icons/logo.svg?react";
import UserIcon from "@icons/user.svg?react";
import { Button } from "@ui/button/button";
import { Input } from "@ui/input/input";
import { useModal } from "@ui/modal/modal";
import { Version } from "@ui/version/version";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ResetPasswordModal } from "../reset-password-modal/reset-password-modal";
import styles from "./login.module.scss";
import { type LoginForm, loginSchema } from "./login.schema";

export const Login = () => {
	const [passwordInputType, setPasswordInputType] = useState(true);

	const { loginMutate, loginMutatePending } = useLoginRequest();

	const { t } = useTranslation();
	const { show, handleShow } = useModal();
	const {
		handleSubmit,
		register,
		setValue,
		watch,
		formState: { errors },
	} = useForm<LoginForm>({
		resolver: zodResolver(loginSchema),
		mode: "onBlur",
		defaultValues: {
			isLdap: false,
		},
	});

	const isLdap = watch("isLdap");
	const showLdap = APPLICATION_CONFIG.show_ldap;

	// `register` returns native `min`/`max` typed as `string | number`, which
	// conflicts with the Input's `min`/`max` (`number`). They are never
	// populated here (no validation config), so drop them from the spread.
	const { min: _emailMin, max: _emailMax, ...emailField } = register("email");
	const {
		min: _passwordMin,
		max: _passwordMax,
		...passwordField
	} = register("password");

	const handleForm = async (data: LoginForm) => {
		loginMutate({
			login: data.email,
			password: data.password,
			isLdap: data.isLdap,
		});
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.icon}>
					<Logo />
				</div>
				<div className={styles.main_wrapper}>
					<div className={styles.main}>
						<div className={styles.title_container}>
							<h2 className={styles.title}>{t("login.title")}</h2>
							<Version />
						</div>

						<form onSubmit={handleSubmit(handleForm)} className={styles.form}>
							{showLdap && (
								<div className={styles.segmented_control}>
									<div
										className={clsx(styles.slider, { [styles.move]: isLdap })}
									/>
									<button
										data-testid="login-local-toggle-btn"
										type="button"
										className={clsx(styles.segment, {
											[styles.active]: !isLdap,
										})}
										onClick={() => setValue("isLdap", false)}
									>
										<UserIcon />
										{t("login.local_label")}
									</button>
									<button
										data-testid="login-ldap-toggle-btn"
										type="button"
										className={clsx(styles.segment, {
											[styles.active]: isLdap,
										})}
										onClick={() => setValue("isLdap", true)}
									>
										<GlobalIcon />
										{t("login.ldap_label")}
									</button>
								</div>
							)}

							<Input
								data-testid="login-email-input"
								error={Boolean(errors.email)}
								errorText={errors.email?.message ?? ""}
								label={t("form.email.label")}
								placeholder={t("form.email.placeholder")}
								id="email"
								{...emailField}
							/>
							<Input
								data-testid="login-password-input"
								type={passwordInputType ? "password" : "text"}
								error={Boolean(errors.password)}
								errorText={errors.password?.message}
								label={t("form.password.label")}
								placeholder={t("form.password.placeholder")}
								id="password"
								endAdornment={
									<EyeIcon
										data-testid="login-password-visibility-toggle"
										onClick={() => setPasswordInputType((prev) => !prev)}
										className={styles.eye_icon}
									/>
								}
								{...passwordField}
							/>

							<div className={styles.button_group}>
								<Button
									data-testid="login-submit-btn"
									isLoading={loginMutatePending}
									type="submit"
								>
									{t("login.login")}
								</Button>
								<button
									data-testid="login-forgot-password-btn"
									onClick={handleShow}
									type="button"
									className={styles.forget_password}
								>
									{t("login.forgetPassword")}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
			<ResetPasswordModal show={show} handleShow={handleShow} />
		</>
	);
};
